import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const DRAFT_PREFIX = "wm_draft_";
const DRAFT_TTL_MS = 60 * 60 * 1000; // 1 hour

// Sweep expired drafts (per-tab, cheap) so localStorage doesn't grow unbounded.
const purgeStaleDrafts = () => {
  try {
    const now = Date.now();
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(DRAFT_PREFIX)) continue;
      try {
        const v = JSON.parse(localStorage.getItem(k) || "null");
        if (!v || !v._createdAt || now - v._createdAt > DRAFT_TTL_MS) toRemove.push(k);
      } catch {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};

/**
 * Reads a preview draft from localStorage when the URL contains `?preview=<key>`.
 * Used by public pages (Brand, News) so the CMS "Preview" button can render
 * unsaved changes in a new tab without touching the database.
 *
 * Returns `{ draft, isPreview }`.
 * - `draft`     : parsed localStorage payload, or null
 * - `isPreview` : true when the URL has ?preview= and a matching draft exists
 */
const usePreviewDraft = () => {
  const { search } = useLocation();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    purgeStaleDrafts();
    const params = new URLSearchParams(search);
    const key = params.get("preview");
    if (!key) {
      setDraft(null);
      return;
    }
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        setDraft(null);
        return;
      }
      setDraft(JSON.parse(raw));
    } catch {
      setDraft(null);
    }
  }, [search]);

  // Toggle a body class so global fixed elements (Navbar) can shift down for the banner.
  useEffect(() => {
    if (draft) document.body.classList.add("preview-mode");
    else document.body.classList.remove("preview-mode");
    return () => document.body.classList.remove("preview-mode");
  }, [draft]);

  return { draft, isPreview: !!draft };
};

export default usePreviewDraft;
